import React from 'react';
import { StatsTableRow } from './StatsTableRow';
import { TeamStats } from '../types';
interface StatsTableProps {
  teamStats: TeamStats;
  selectedStat: string | null;
  selectedLocation: 'overall' | 'home' | 'away' | null;
  onStatSelect: (stat: string, location: 'overall' | 'home' | 'away') => void;
}
export const StatsTable: React.FC<StatsTableProps> = ({
  teamStats,
  selectedStat,
  selectedLocation,
  onStatSelect
}) => {
  const calculateHomePercentage = (value: number) => {
    return teamStats.homeMatches > 0 ? (value / teamStats.homeMatches * 100).toFixed(1) : '0';
  };
  const calculateAwayPercentage = (value: number) => {
    return teamStats.awayMatches > 0 ? (value / teamStats.awayMatches * 100).toFixed(1) : '0';
  };
  const statRows = [{
    label: 'Matches',
    overall: teamStats.played,
    home: teamStats.homeMatches,
    away: teamStats.awayMatches
  }, {
    label: 'Points',
    overall: teamStats.wins * 3 + teamStats.draws,
    home: teamStats.fullTimeStats.homeWins * 3 + teamStats.fullTimeStats.homeDraws,
    away: teamStats.fullTimeStats.awayWins * 3 + teamStats.fullTimeStats.awayDraws
  }, {
    label: 'Wins',
    overall: `${teamStats.wins} (${(teamStats.wins / teamStats.played * 100).toFixed(0)}%)`,
    home: `${teamStats.fullTimeStats.homeWins} (${calculateHomePercentage(teamStats.fullTimeStats.homeWins)}%)`,
    away: `${teamStats.fullTimeStats.awayWins} (${calculateAwayPercentage(teamStats.fullTimeStats.awayWins)}%)`,
    color: 'text-green-600',
    onClick: (location: 'overall' | 'home' | 'away') => onStatSelect('wins', location),
    isSelected: selectedStat === 'wins'
  }, {
    label: 'Draws',
    overall: `${teamStats.draws} (${(teamStats.draws / teamStats.played * 100).toFixed(0)}%)`,
    home: `${teamStats.fullTimeStats.homeDraws} (${calculateHomePercentage(teamStats.fullTimeStats.homeDraws)}%)`,
    away: `${teamStats.fullTimeStats.awayDraws} (${calculateAwayPercentage(teamStats.fullTimeStats.awayDraws)}%)`,
    color: 'text-yellow-600',
    onClick: (location: 'overall' | 'home' | 'away') => onStatSelect('draws', location),
    isSelected: selectedStat === 'draws'
  }, {
    label: 'Losses',
    overall: `${teamStats.losses} (${(teamStats.losses / teamStats.played * 100).toFixed(0)}%)`,
    home: `${teamStats.fullTimeStats.homeLosses} (${calculateHomePercentage(teamStats.fullTimeStats.homeLosses)}%)`,
    away: `${teamStats.fullTimeStats.awayLosses} (${calculateAwayPercentage(teamStats.fullTimeStats.awayLosses)}%)`,
    color: 'text-red-600',
    onClick: (location: 'overall' | 'home' | 'away') => onStatSelect('losses', location),
    isSelected: selectedStat === 'losses'
  }, {
    label: 'Half Time Wins',
    overall: `${teamStats.halfTimeStats.homeWins + teamStats.halfTimeStats.awayWins} (${((teamStats.halfTimeStats.homeWins + teamStats.halfTimeStats.awayWins) / teamStats.played * 100).toFixed(0)}%)`,
    home: `${teamStats.halfTimeStats.homeWins} (${calculateHomePercentage(teamStats.halfTimeStats.homeWins)}%)`,
    away: `${teamStats.halfTimeStats.awayWins} (${calculateAwayPercentage(teamStats.halfTimeStats.awayWins)}%)`,
    color: 'text-green-600',
    onClick: (location: 'overall' | 'home' | 'away') => onStatSelect('halfTimeWins', location),
    isSelected: selectedStat === 'halfTimeWins'
  }, {
    label: 'Half Time Draws',
    overall: `${teamStats.halfTimeStats.homeDraws + teamStats.halfTimeStats.awayDraws} (${((teamStats.halfTimeStats.homeDraws + teamStats.halfTimeStats.awayDraws) / teamStats.played * 100).toFixed(0)}%)`,
    home: `${teamStats.halfTimeStats.homeDraws} (${calculateHomePercentage(teamStats.halfTimeStats.homeDraws)}%)`,
    away: `${teamStats.halfTimeStats.awayDraws} (${calculateAwayPercentage(teamStats.halfTimeStats.awayDraws)}%)`,
    color: 'text-yellow-600',
    onClick: (location: 'overall' | 'home' | 'away') => onStatSelect('halfTimeDraws', location),
    isSelected: selectedStat === 'halfTimeDraws'
  }, {
    label: 'Half Time Losses',
    overall: `${teamStats.halfTimeStats.homeLosses + teamStats.halfTimeStats.awayLosses} (${((teamStats.halfTimeStats.homeLosses + teamStats.halfTimeStats.awayLosses) / teamStats.played * 100).toFixed(0)}%)`,
    home: `${teamStats.halfTimeStats.homeLosses} (${calculateHomePercentage(teamStats.halfTimeStats.homeLosses)}%)`,
    away: `${teamStats.halfTimeStats.awayLosses} (${calculateAwayPercentage(teamStats.halfTimeStats.awayLosses)}%)`,
    color: 'text-red-600',
    onClick: (location: 'overall' | 'home' | 'away') => onStatSelect('halfTimeLosses', location),
    isSelected: selectedStat === 'halfTimeLosses'
  }, {
    label: 'Clean Sheets',
    overall: `${teamStats.fullTimeStats.homeCleanSheets + teamStats.fullTimeStats.awayCleanSheets} (${((teamStats.fullTimeStats.homeCleanSheets + teamStats.fullTimeStats.awayCleanSheets) / teamStats.played * 100).toFixed(0)}%)`,
    home: `${teamStats.fullTimeStats.homeCleanSheets} (${calculateHomePercentage(teamStats.fullTimeStats.homeCleanSheets)}%)`,
    away: `${teamStats.fullTimeStats.awayCleanSheets} (${calculateAwayPercentage(teamStats.fullTimeStats.awayCleanSheets)}%)`,
    color: 'text-blue-600',
    onClick: (location: 'overall' | 'home' | 'away') => onStatSelect('cleanSheets', location),
    isSelected: selectedStat === 'cleanSheets'
  }, {
    label: 'BTTS',
    overall: `${teamStats.fullTimeStats.homeBTTS + teamStats.fullTimeStats.awayBTTS} (${((teamStats.fullTimeStats.homeBTTS + teamStats.fullTimeStats.awayBTTS) / teamStats.played * 100).toFixed(0)}%)`,
    home: `${teamStats.fullTimeStats.homeBTTS} (${calculateHomePercentage(teamStats.fullTimeStats.homeBTTS)}%)`,
    away: `${teamStats.fullTimeStats.awayBTTS} (${calculateAwayPercentage(teamStats.fullTimeStats.awayBTTS)}%)`,
    color: 'text-purple-600',
    onClick: (location: 'overall' | 'home' | 'away') => onStatSelect('btts', location),
    isSelected: selectedStat === 'btts'
  }, {
    label: 'HT BTTS',
    overall: `${teamStats.halfTimeStats.homeBTTS + teamStats.halfTimeStats.awayBTTS} (${((teamStats.halfTimeStats.homeBTTS + teamStats.halfTimeStats.awayBTTS) / teamStats.played * 100).toFixed(0)}%)`,
    home: `${teamStats.halfTimeStats.homeBTTS} (${calculateHomePercentage(teamStats.halfTimeStats.homeBTTS)}%)`,
    away: `${teamStats.halfTimeStats.awayBTTS} (${calculateAwayPercentage(teamStats.halfTimeStats.awayBTTS)}%)`,
    color: 'text-purple-600',
    onClick: (location: 'overall' | 'home' | 'away') => onStatSelect('htBtts', location),
    isSelected: selectedStat === 'htBtts'
  }];
  return <div className="bg-white rounded-lg shadow overflow-hidden">
      <table className="min-w-full">
        <thead>
          <tr className="bg-gray-50 border-b">
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
          </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Overall</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Home</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Away</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {statRows.map((row, index) => <StatsTableRow key={index} label={row.label} overall={row.overall} home={row.home} away={row.away} color={row.color} onClick={row.onClick} isSelected={row.isSelected} selectedLocation={selectedLocation} />)}
        </tbody>
      </table>
    </div>;
};